import {
  Alert,
  Button,
  EmptyState,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { FileText, Plus } from 'lucide-react';
import type { JSX } from 'react';

import { useCurrentUser } from '../../../auth/use-current-user';
import { hasPermission } from '../../../auth/has-permission';
import { type ContractListItem, listContracts } from '../../../contracts/api';
import { formatCalendarDate } from '../../../contracts/format-calendar-date';

export interface CustomerContractsTabProps {
  customerId: string;
}

/**
 * `contracts/spec.md` amendment: a customer's own contracts, visible from
 * its detail page. Reuses `GET /contracts?customerId=…`
 * (`contracts/api.ts`) rather than a dedicated endpoint, same criterion
 * `CustomerSitesTab`/`CustomerContactsTab` already apply to their own
 * sub-resources.
 */
export function CustomerContractsTab({ customerId }: CustomerContractsTabProps): JSX.Element {
  const currentUser = useCurrentUser();
  const canCreate = hasPermission(currentUser.data, 'contract.create');

  const contractsQuery = useQuery({
    queryKey: ['customer-contracts', customerId],
    queryFn: () => listContracts(customerId),
  });

  if (contractsQuery.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (contractsQuery.isError) {
    return (
      <Alert tone="danger" title="No pudimos cargar los contratos">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void contractsQuery.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const contracts = contractsQuery.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Contratos</h2>
        {canCreate ? (
          <Link to="/contracts/new" search={{ customerId }}>
            <Button icon={Plus}>Nuevo contrato</Button>
          </Link>
        ) : null}
      </div>

      {contracts.length === 0 ? (
        <EmptyState icon={FileText} title="Esta empresa todavía no tiene contratos registrados." />
      ) : (
        <Table>
          <caption className="sr-only">Contratos de la empresa</caption>
          <thead>
            <tr>
              <TableHeader>Código</TableHeader>
              <TableHeader>Versión</TableHeader>
              <TableHeader>Vigencia</TableHeader>
              <TableHeader>Estado</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {contracts.map((contract) => (
              <TableRow key={contract.id}>
                <TableCell>
                  <Link
                    to="/contracts/$contractId"
                    params={{ contractId: contract.id }}
                    className="font-medium text-slate-900 hover:underline"
                  >
                    {contract.code}
                  </Link>
                </TableCell>
                <TableCell>{contract.version}</TableCell>
                <TableCell>{formatValidity(contract)}</TableCell>
                <TableCell>
                  <StatusBadge domain="contract" status={contract.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function formatValidity(contract: ContractListItem): string {
  const start = formatCalendarDate(contract.startDate);

  if (contract.endDate === null) {
    return `Desde ${start}`;
  }

  return `${start} – ${formatCalendarDate(contract.endDate)}`;
}
